import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate } from "react-router-dom";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { NetworkFormErrors, NetworkErrors, useCreateNetwork } from "./networkUtils";
import { useAllSponsors } from "../Sponsors/sponsorUtils";

const pageDetails: PageWrapperProps = {
  className: "NewNetwork",
  headerText: "New Network",
  noScroll: true
};

type NetworkFormInputs = {
  name: string;
  sponsorId: string;
  acgmeEnabled: boolean;
  allAttendingHistoryDisabled: boolean;
  dashboardUrl: string;
  showDashboardLink: boolean;
  traineeCanRateTrainee: boolean;
  feedbackMinWords: number | null;
};

export const NewNetwork = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sponsors, status: sponsorsStatus } = useAllSponsors();
  const {
    status: createNetworkStatus,
    error: createNetworkError,
    createNetwork
  } = useCreateNetwork();

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<NetworkFormInputs>({
    defaultValues: {
      name: "",
      sponsorId: "",
      acgmeEnabled: false,
      allAttendingHistoryDisabled: false,
      dashboardUrl: "",
      showDashboardLink: false,
      traineeCanRateTrainee: false,
      feedbackMinWords: null
    }
  });

  const alphabetizedSponsors = sponsors
    ? [...sponsors].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "")
      )
    : sponsors;

  const anyApiIsLoading = [sponsorsStatus, createNetworkStatus].includes(
    "loading"
  );

  const onSubmit: SubmitHandler<NetworkFormInputs> = async (data) => {
    const result = await createNetwork({
      name: data.name,
      sponsorId: data.sponsorId,
      acgmeEnabled: data.acgmeEnabled,
      allAttendingHistoryDisabled: data.allAttendingHistoryDisabled,
      simpl1Id: null,
      dashboardUrl: data.dashboardUrl?.trim() || null,
      showDashboardLink: data.showDashboardLink,
      traineeCanRateTrainee: data.traineeCanRateTrainee,
      feedbackMinWords:
        data.feedbackMinWords && data.feedbackMinWords > 0
          ? data.feedbackMinWords
          : null
    });

    if (result !== null) {
      // Land on the edit page so the SysAdmin can configure this network's
      // identifiers right after creating it (identifier config requires the
      // network to exist).
      navigate(`/networks/${result.id}/edit`);
    } else {
      console.error("Failed to create network: API returned null");
    }
  };

  if (sponsorsStatus === "loading") {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (createNetworkStatus === "failed" || createNetworkError) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error")}</h2>
        <h3>{t(NetworkErrors.CreateNetworkError)}</h3>
        {createNetworkError && <p>{createNetworkError}</p>}
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...pageDetails}>
      <Box
        component="form"
        className="NewNetworkForm"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ maxWidth: 760, mt: 2 }}
      >
        <Grid container spacing={2} rowSpacing={2.5}>
          <Grid xs={12} sm={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: NetworkFormErrors.EmptyNetworkName }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  label={t("Network Name")}
                  size="small"
                  required
                  fullWidth
                  disabled={anyApiIsLoading}
                  error={!!errors.name}
                  helperText={
                    errors.name ? t(NetworkFormErrors.EmptyNetworkName) : ""
                  }
                />
              )}
            />
          </Grid>

          <Grid xs={12} sm={6}>
            <Controller
              name="sponsorId"
              control={control}
              rules={{ required: NetworkFormErrors.NoSponsor }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  value={field.value ?? ""}
                  label={t("Sponsor")}
                  size="small"
                  required
                  fullWidth
                  disabled={anyApiIsLoading}
                  error={!!errors.sponsorId}
                  helperText={
                    errors.sponsorId ? t(NetworkFormErrors.NoSponsor) : ""
                  }
                >
                  <MenuItem value="" disabled>
                    {t("Select a sponsor")}
                  </MenuItem>
                  {alphabetizedSponsors?.map((sponsor) => (
                    <MenuItem key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid xs={12} sm={6}>
            <Controller
              name="dashboardUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  label={t("Dashboard URL")}
                  placeholder="https://…"
                  size="small"
                  fullWidth
                  disabled={anyApiIsLoading}
                />
              )}
            />
          </Grid>

          <Grid xs={12} sm={6}>
            <Controller
              name="feedbackMinWords"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("Feedback Minimum Word Count")}
                  type="number"
                  size="small"
                  fullWidth
                  disabled={anyApiIsLoading}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(
                      v === "" ? null : Math.max(0, parseInt(v, 10) || 0)
                    );
                  }}
                  inputProps={{ min: 0 }}
                  helperText={t(
                    "Minimum words required in narrative Feedback answers (blank = no minimum)."
                  )}
                />
              )}
            />
          </Grid>

          <Grid xs={12}>
            <FormGroup row sx={{ columnGap: 3, rowGap: 0 }}>
              <Controller
                name="acgmeEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={anyApiIsLoading}
                      />
                    }
                    label={t("ACGME Enabled")}
                  />
                )}
              />
              <Controller
                name="allAttendingHistoryDisabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={anyApiIsLoading}
                      />
                    }
                    label={t("All Attending History Disabled")}
                  />
                )}
              />
              <Controller
                name="traineeCanRateTrainee"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={anyApiIsLoading}
                      />
                    }
                    label={t("Trainee Can Rate Trainee")}
                  />
                )}
              />
              <Controller
                name="showDashboardLink"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={anyApiIsLoading}
                      />
                    }
                    label={t("Show Dashboard Link")}
                  />
                )}
              />
            </FormGroup>
          </Grid>

          <Grid xs={12}>
            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={anyApiIsLoading}
              >
                {t("Save")}
              </Button>
              <Button
                type="button"
                variant="outlined"
                disabled={anyApiIsLoading}
                onClick={() => navigate("/networks")}
              >
                {t("Cancel")}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </PageWrapper>
  );
};
